<template>
  <div>
    <!-- Header + CTA -->
    <div class="d-flex justify-content-between align-items-center mb-6">
      <h3 class="mb-0">Gestão de Contratos</h3>
      <b-button variant="primary" @click="$router.push({ name: 'contratosServicosNovo' })">
        <i class="la la-file-contract mr-2"></i> Novo Contrato
      </b-button>
    </div>

    <!-- Cards resumo -->
    <b-row class="mb-6" v-if="currentUser.admin">
      <!-- Total no período -->
      <b-col md="2" class="mb-4 d-flex">
        <div class="card card-custom shadow-sm w-100 h-100">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <div class="text-muted">Total no período</div>
              <small class="text-muted d-block">{{ totalPeriodoLabel }}</small>
              <template v-if="loadingKpis">
                <b-skeleton width="60px" height="28px" class="mt-1" />
              </template>
              <template v-else>
                <div class="font-weight-bolder h4 mb-0">
                  {{ kpis.totalJanela }}
                </div>
              </template>
            </div>
            <i class="la la-calendar-check-o icon-2x"></i>
          </div>
        </div>
      </b-col>

      <!-- Ativos -->
      <b-col md="2" class="mb-4 d-flex">
        <div class="card card-custom shadow-sm w-100 h-100">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <div class="text-muted">Ativos</div>
              <template v-if="loadingKpis">
                <b-skeleton width="60px" height="28px" class="mt-1" />
              </template>
              <template v-else>
                <div class="font-weight-bolder h4 mb-0">{{ kpis.ativos }}</div>
              </template>
            </div>
            <i class="la la-check-circle icon-2x"></i>
          </div>
        </div>
      </b-col>

      <!-- A vencer -->
      <b-col md="2" class="mb-4 d-flex">
        <div class="card card-custom shadow-sm w-100 h-100">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <div class="text-muted">{{ aVencerLabel }}</div>
              <template v-if="loadingKpis">
                <b-skeleton width="60px" height="28px" class="mt-1" />
              </template>
              <template v-else>
                <div class="font-weight-bolder h4 mb-0">
                  {{ kpis.aVencer30 }}
                </div>
              </template>
            </div>
            <i class="la la-hourglass-half icon-2x"></i>
          </div>
        </div>
      </b-col>

      <!-- Vencidos -->
      <b-col md="2" class="mb-4 d-flex">
        <div class="card card-custom shadow-sm w-100 h-100">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <div class="text-muted">Vencidos</div>
              <template v-if="loadingKpis">
                <b-skeleton width="60px" height="28px" class="mt-1" />
              </template>
              <template v-else>
                <div class="font-weight-bolder h4 mb-0 text-danger">
                  {{ kpis.vencidos }}
                </div>
              </template>
            </div>
            <i class="la la-times-circle icon-2x"></i>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Filtros Ultra-Compactos (uma linha no desktop) -->
    <div class="card card-custom mb-3">
      <div class="card-body py-2 px-3">
        <b-form @submit.prevent="applyFilters">
          <b-row no-gutters class="align-items-center flex-nowrap">
            <!-- Busca -->
            <b-col cols="auto" class="pr-2">
              <b-input-group size="sm" class="w-auto">
                <b-form-input v-model="searchTerm" placeholder="Cliente / CPF/CNPJ" class="wch-28" inputmode="search"
                  autocomplete="off" trim />
                <b-input-group-append>
                  <b-button size="sm" variant="outline-secondary" @click="searchTerm = ''" v-b-tooltip.hover
                    title="Limpar">
                    <i class="la la-times"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>

            <!-- Status -->
            <b-col cols="auto" class="px-2">
              <b-form-select v-model="filters.status" :options="statusOptions" size="sm" class="wch-16" />
            </b-col>

            <!-- Período -->
            <b-col cols="auto" class="px-2 d-flex align-items-center">
              <b-form-datepicker v-model="filters.dateFrom" size="sm" class="mr-2 wch-14" :date-format-options="{
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
              }" placeholder="Início" :reset-button="false" />
              <span class="mx-1">—</span>
              <b-form-datepicker v-model="filters.dateTo" size="sm" class="ml-2 wch-14" :date-format-options="{
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
              }" placeholder="Fim" :reset-button="false" />
              <b-button size="sm" variant="link" class="p-0 ml-2" @click="clearDateRange" v-b-tooltip.hover
                title="Limpar período">
                <i class="la la-eraser"></i>
              </b-button>
            </b-col>

            <!-- Ação -->
            <b-col cols="auto" class="pl-2">
              <b-button size="sm" variant="primary" type="submit">
                <i class="la la-search mr-1"></i> Filtrar
              </b-button>
            </b-col>
          </b-row>

          <!-- Linha de ajuda (opcional, ocupa 0 no desktop e quebra no mobile) -->
          <small class="text-muted d-none d-md-inline ml-2" v-if="filters.dateFrom || filters.dateTo">
            {{ periodLabel }}
          </small>
        </b-form>
      </div>
    </div>

    <!-- Tabela -->
    <div class="card card-custom">
      <!-- Barra de ações em massa -->
      <div v-if="selectedCount > 0" class="alert alert-primary d-flex justify-content-between align-items-center mb-3">
        <div>
          <strong>{{ selectedCount }}</strong> contrato(s) selecionado(s).
        </div>
        <div class="btn-group">
          <b-button v-if="canExecuteSelected" size="sm" variant="success" @click="openExecuteModal(firstSelected)">
            <i class="la la-play mr-1"></i> Executar agora
          </b-button>
          <b-button v-if="currentEmpresaId == 1" size="sm" variant="secondary" @click="openAtivarLicencaModal">
            <i class="la la-check mr-1"></i> Ativar licença
          </b-button>
          <b-button v-if="canActivateSelected" size="sm" variant="success"
            @click="$bvModal.show('confirm-bulk-ativar')">
            <i class="la la-check mr-1"></i> Ativar
          </b-button>
          <b-button v-if="currentEmpresaId == 1" size="sm" variant="secondary"
            @click="$bvModal.show('confirm-bulk-congelar')">
            <i class="la la-snowflake-o mr-1"></i> Congelar contrato
          </b-button>
          <b-button size="sm" variant="warning" @click="openAlterarDiaModal">
            <i class="la la-calendar mr-1"></i> Alterar dia de cobrança
          </b-button>
          <b-button size="sm" variant="info" @click="openAlterarDiaEnvioModal">
            <i class="la la-send mr-1"></i> Alterar dia de envio
          </b-button>
          <b-button size="sm" variant="danger" @click="$bvModal.show('confirm-bulk-cancelar')">
            <i class="la la-ban mr-1"></i> Cancelar
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="clearSelection">
            Limpar seleção
          </b-button>
        </div>
      </div>

      <b-modal cancel-title="Cancelar" id="confirma-dia-cobranca" title="Alterar dia de cobrança" centered
        :ok-disabled="!diaCobrancaForm.day || diaCobrancaLoading"
        :ok-title="diaCobrancaLoading ? 'Salvando...' : 'Confirmar'" :cancel-disabled="diaCobrancaLoading"
        :busy="diaCobrancaLoading" @ok.prevent="handleAlterarDiaCobranca" @hidden="resetDiaCobrancaModal">
        <p class="mb-3">
          Você vai alterar o dia de cobrança de
          <strong>{{ selectedCount }}</strong> contrato(s).
        </p>

        <b-form-group label="Novo dia de cobrança">
          <b-form-select v-model="diaCobrancaForm.day" :options="dayOptions" />
          <small class="text-muted d-block mt-2">
            Se o mês não tiver o dia escolhido, a cobrança ocorrerá no último
            dia disponível do mês.
          </small>
        </b-form-group>
      </b-modal>

      <b-modal cancel-title="Cancelar" id="confirma-dia-envio-fatura" title="Alterar dia de envio da fatura"
        centered :ok-disabled="!diaEnvioFaturaForm.day || diaEnvioFaturaLoading"
        :ok-title="diaEnvioFaturaLoading ? 'Salvando...' : 'Confirmar'"
        :cancel-disabled="diaEnvioFaturaLoading" :busy="diaEnvioFaturaLoading"
        @ok.prevent="handleAlterarDiaEnvioFatura" @hidden="resetDiaEnvioFaturaModal">
        <p class="mb-3">
          Você vai alterar o dia de envio da fatura de
          <strong>{{ selectedCount }}</strong> contrato(s).
        </p>

        <b-form-group label="Novo dia de envio da fatura">
          <b-form-select v-model="diaEnvioFaturaForm.day" :options="dayOptions" />
          <small class="text-muted d-block mt-2">
            Se o mês não tiver o dia escolhido, a fatura será enviada no último
            dia disponível do mês.
          </small>
        </b-form-group>
      </b-modal>

      <b-modal id="confirm-bulk-cancelar" title="Confirmar cancelamento" centered :ok-disabled="cancelLoading"
        :ok-title="cancelLoading ? 'Cancelando...' : 'Sim, cancelar'" ok-variant="danger" cancel-title="Cancelar"
        :cancel-disabled="cancelLoading" :busy="cancelLoading" @ok.prevent="handleCancelar"
        @hidden="cancelLoading = false">
        <p class="mb-0">
          Tem certeza que deseja <strong>cancelar</strong>, após cancelar os
          boletos em abertos serão cancelados.
          <strong> {{ selectedCount }}</strong> contrato(s) selecionado(s)?
        </p>
      </b-modal>

      <b-modal cancel-title="Cancelar" id="confirm-bulk-ativar" title="Confirmar ativação" centered
        :ok-disabled="confirmLoading" :ok-title="confirmLoading ? 'Ativando...' : 'Sim, ativar'" :ok-variant="'success'"
        :cancel-disabled="confirmLoading" :busy="confirmLoading" @ok.prevent="handleConfirmAtivar">
        Tem certeza que deseja ativar os contratos selecionados?
      </b-modal>

      <b-modal cancel-title="Cancelar" id="confirm-bulk-congelar" title="Confirmar congelamento do contrato" centered
        :ok-disabled="confirmLoading" :ok-title="confirmLoading ? 'Congelando...' : 'Sim, congelar'"
        :ok-variant="'success'" :cancel-disabled="confirmLoading" :busy="confirmLoading"
        @ok.prevent="handleConfirmCongela">
        Tem certeza que deseja congelar os contratos selecionados?
      </b-modal>

      <b-modal id="confirm-execute-contract" title="Executar contrato agora" centered
        :ok-disabled="executeLoading" :cancel-disabled="executeLoading" :busy="executeLoading"
        :ok-title="executeLoading ? 'Executando...' : 'Sim, executar agora'" ok-variant="success"
        cancel-title="Cancelar" @ok.prevent="handleExecuteNow" @hidden="resetExecuteModal">
        <p v-if="executionTarget" class="mb-2">
          O contrato <strong>#{{ executionTarget.numero }}</strong> de
          <strong>{{ executionTarget.cliente }}</strong> será faturado agora.
        </p>
        <div class="alert alert-light-primary mb-3">
          A NFS-e e a cobrança serão geradas para a competência vigente:
          <strong>{{ currentCompetenciaLabel }}</strong>.
        </div>
        <p class="text-muted mb-0">
          Esta ação poderá emitir a NFS-e e gerar o boleto configurado. Se já existir uma cobrança ativa nesta
          competência, nada será gerado novamente e você receberá um aviso.
        </p>
      </b-modal>

      <b-modal id="generate-period-boletos-modal" title="Gerar boletos do período" centered
        :ok-disabled="periodBoletos.loading" :cancel-disabled="periodBoletos.loading"
        :busy="periodBoletos.loading"
        :ok-title="periodBoletos.loading ? 'Gerando...' : 'Gerar boletos'" ok-variant="primary"
        cancel-title="Cancelar" @ok.prevent="handleGeneratePeriodBoletos" @hidden="resetPeriodBoletosModal">
        <p v-if="periodBoletos.contrato" class="mb-2">
          Contrato <strong>#{{ periodBoletos.contrato.numero }}</strong> ·
          {{ periodBoletos.contrato.cliente }}
        </p>
        <div v-if="periodBoletos.contrato" class="alert alert-light-primary mb-2">
          Serão gerados os boletos mensais a partir do mês seguinte ao início da vigência
          (<strong>{{ formatDate(periodBoletos.contrato.data_inicio) }}</strong>) e até
          <strong>{{ formatDate(periodBoletos.contrato.data_termino) }}</strong>.
        </div>
        <small class="text-muted">
          Boletos já existentes serão mantidos. A rotina mensal continuará gerando somente a NFS-e de cada mês.
        </small>
      </b-modal>

      <b-modal id="send-contract-invoice-modal" title="Enviar fatura" centered
        :ok-disabled="invoiceEmail.loading || invoiceEmail.sending || !isInvoiceEmailValid || !invoiceEmail.hasDocuments"
        :cancel-disabled="invoiceEmail.sending" :busy="invoiceEmail.sending"
        :ok-title="invoiceEmail.sending ? 'Enviando...' : 'Enviar fatura'" cancel-title="Cancelar"
        @ok.prevent="handleSendInvoiceEmail" @hidden="resetInvoiceEmailModal">
        <p v-if="invoiceEmail.contrato" class="text-muted mb-3">
          Contrato #{{ invoiceEmail.contrato.numero }} ·
          {{ invoiceEmail.contrato.cliente }}
        </p>

        <b-form-group label="Competência" label-for="invoice-email-competencia">
          <b-form-input id="invoice-email-competencia" v-model="invoiceEmail.competencia" type="month"
            :disabled="invoiceEmail.loading || invoiceEmail.sending" @change="loadInvoiceEmailPreview(false)" />
        </b-form-group>

        <b-form-group label="E-mail de destino" label-for="invoice-email-address">
          <b-form-input id="invoice-email-address" v-model.trim="invoiceEmail.email" type="email"
            placeholder="cliente@empresa.com" :disabled="invoiceEmail.sending" />
          <small class="text-muted">O e-mail do contrato já vem preenchido, mas pode ser alterado para este envio.</small>
        </b-form-group>

        <div v-if="invoiceEmail.loading" class="text-muted py-2">
          <b-spinner small class="mr-2" /> Consultando documentos...
        </div>

        <div v-else-if="invoiceEmail.loadError" class="alert alert-danger mb-0">
          {{ invoiceEmail.loadError }}
        </div>

        <div v-else-if="!invoiceEmail.hasDocuments" class="alert alert-warning mb-0">
          Nenhuma NFS-e ou boleto disponível nesta competência.
        </div>

        <div v-else class="alert alert-light-primary mb-0">
          <strong class="mr-2">Será enviado:</strong>
          <b-badge v-if="invoiceEmail.nfses.length" variant="success" class="mr-2">
            {{ invoiceEmail.nfses.length }} NFS-e
          </b-badge>
          <b-badge v-if="invoiceEmail.boletos.length" variant="primary">
            {{ invoiceEmail.boletos.length }}
            {{ invoiceEmail.boletos.length === 1 ? "boleto" : "boletos" }}
          </b-badge>
        </div>
      </b-modal>

      <b-modal cancel-title="Cancelar" id="confirm-ativar-licenca" title="Confirmar ativação da conta" centered
        :ok-disabled="!licencaForm.plano || confirmLoading" :ok-title="confirmLoading ? 'Ativando...' : 'Sim, ativar'"
        ok-variant="success" :cancel-disabled="confirmLoading" :busy="confirmLoading"
        @ok.prevent="handleConfirmAtivarLicenca" @hidden="licencaForm.plano = null">
        <p class="mb-3">
          Tem certeza que deseja ativar a licença do
          <strong>
            {{ firstSelected?.cliente }} —
            {{ maskDoc(firstSelected?.cnpj_cpf) }}
          </strong>
          por mais 1 ano?
        </p>

        <b-form-group label="Plano da licença">
          <b-form-select v-model="licencaForm.plano" :options="planoOptions" placeholder="Selecione um plano" />
        </b-form-group>
      </b-modal>

      <div class="card-body p-0">
        <b-table :items="rows" :fields="fields" :busy="loading" hover responsive primary-key="id_contrato_fatura"
          head-variant="light" :no-local-sorting="true" @sort-changed="onSortChanged" :sort-by.sync="sort.field"
          :sort-desc.sync="sort.typeDesc" :tbody-tr-class="rowClass" :show-empty="true">
          <template #table-busy>
            <div class="text-center p-5">Carregando...</div>
          </template>

          <!-- MOSTRA QUANDO NÃO HÁ REGISTROS (sem filtro) -->
          <template #empty>
            <div class="text-center text-muted py-5">
              <i class="la la-folder-open mb-2" style="font-size: 28px"></i>
              <div>Nenhum contrato até o momento.</div>
            </div>
          </template>

          <!-- MOSTRA QUANDO NÃO HÁ REGISTROS DEPOIS DE FILTRAR -->
          <template #emptyfiltered>
            <div class="text-center text-muted py-5">
              <i class="la la-search mb-2" style="font-size: 28px"></i>
              <div>Nenhum contrato encontrado com os filtros atuais.</div>
            </div>
          </template>

          <!-- Cabeçalho: checkbox de selecionar todos da página -->
          <template #head(select)>
            <b-form-checkbox :indeterminate="someSelectedOnPage" :checked="allSelectedOnPage"
              @change="toggleSelectAllOnPage" aria-label="Selecionar todos" />
          </template>

          <!-- Célula: checkbox por linha -->
          <template #cell(select)="{ item }">
            <b-form-checkbox :disabled="isRowDisabled(item)" :checked="isRowSelected(item)"
              @change="() => toggleRow(item)" :aria-label="`Selecionar contrato #${item.numero}`" />
          </template>

          <template #cell(contrato)="{ item }">
            <div class="font-weight-600">
              #{{ item.numero }}
              <small class="text-muted ml-2 d-block" v-if="currentEmpresaId == 1">
                ({{ planoDict[item.id_plano] || "—" }})
              </small>
              <b-badge v-if="item.solicitacao_cliente" variant="success" class="cursor-pointer"
                @click="abrirLogs(item)">
                Cliente renovou
              </b-badge>
            </div>
            <small class="text-muted" v-if="item.vendedor">
              Vend: {{ item.vendedor.nome }}
            </small>
          </template>

          <template #cell(cliente)="{ item }">
            <div class="font-weight-600">{{ item.cliente }}</div>
            <small class="text-muted">{{ maskDoc(item.cnpj_cpf) }}</small>
          </template>

          <template #cell(venda)="{ item }">
            <div class="font-weight-600">
              {{ formatDate(item.data_venda) }}
            </div>
          </template>

          <template #cell(vigencia)="{ item }">
            {{ formatDate(item.data_inicio) }} —
            {{ formatDate(item.data_termino) }}
            <div>
              <small :class="diasClasse(item.__dias)">{{
                badgeVigencia(item.__dias)
                }}</small>
            </div>
          </template>

          <template #cell(valor)="{ item }">
            <div>{{ formatMoney(item.valor_total) }}</div>
            <small class="text-muted d-inline-flex align-items-center"
              v-if="item.taxa_implantacao && isNuts" v-b-tooltip.hover :title="taxaTooltip(item)">
              {{
                item.taxa_implantacao && Number(item.taxa_implantacao) > 0
                  ? "Taxa: " + formatMoney(item.taxa_implantacao)
                  : "Sem Taxa"
              }}

              <!-- Ícone visual da situação do boleto da taxa -->
              <i class="la ml-1" :class="item.gerar_boleto_taxa
                ? 'la-receipt text-success'
                : 'la-ban text-muted'
                " aria-hidden="true"></i>
            </small>
          </template>

          <template #cell(status)="{ item }">
            <b-badge :variant="statusVariant(item.status)">{{
              mapStatus(item.status)
              }}</b-badge>
          </template>

          <template #cell(cobranca)="{ item }">
            <div class="d-flex align-items-center flex-wrap gap-6">
              <b-badge :variant="item.emitir_nfse ? 'success' : 'secondary'" pill v-b-tooltip.hover
                :title="item.emitir_nfse ? 'Emitir NFSe' : 'Não emitir NFSe'" class="mr-2 mb-1">
                NFSe
              </b-badge>

              <b-badge :variant="item.emitir_boletos ? 'primary' : 'secondary'" pill v-b-tooltip.hover
                :title="item.emitir_boletos ? 'Emite boletos' : 'Não emite boletos'" class="mr-2 mb-1">
                Boleto
              </b-badge>

              <b-badge variant="info" pill v-b-tooltip.left
                :title="`Cobrança no dia ${item.dia_cobranca || '-'} de cada mês`" class="mr-2 mb-1">
                Dia {{ item.dia_cobranca || "-" }}
              </b-badge>

              <b-badge v-if="currentEmpresaId !== 1" variant="dark" pill v-b-tooltip.left
                :title="`Envio da fatura no dia ${item.dia_envio_fatura || 1} de cada mês`" class="mr-2 mb-1">
                Envio {{ item.dia_envio_fatura || 1 }}
              </b-badge>

              <b-badge v-if="item.cobranca_anual" variant="warning" pill v-b-tooltip.left
                title="Vai gerar todos os boletos do contrato" class="mr-2 mb-1">
                Cobrança Anual
              </b-badge>

              <b-badge v-if="item.plugin_certificado && currentEmpresaId == 1" variant="dark" pill v-b-tooltip.hover
                title="Contrato com Certificado Digital A1" class="mr-2 mb-1">
                Certificado
              </b-badge>

              <b-badge v-if="item.plugin_tef && currentEmpresaId == 1" variant="dark" pill v-b-tooltip.hover
                title="Contrato com TEF Integrado" class="mr-2 mb-1">
                TEF
              </b-badge>

              <b-badge v-if="item.carne_asaas" variant="success" pill v-b-tooltip.left title="Carnê completo disponível"
                class="mr-2 mb-1">
                <a :href="item.carne_asaas" target="_blank" rel="noopener noreferrer" class="text-white"
                  style="text-decoration: none">
                  Carnê
                </a>
              </b-badge>
            </div>
          </template>

          <template #cell(acoes)="{ item }">
            <button v-if="canExecuteContract(item)" class="btn btn-xs btn-success btn-icon p-0 mr-1"
              @click="openExecuteModal(item)" v-b-tooltip.righttop="'Executar contrato agora'">
              <i class="la la-play" style="font-size: 15px"></i>
            </button>

            <button v-if="canGeneratePeriodBoletos(item)"
              class="btn btn-xs btn-outline-primary btn-icon p-0 mr-1"
              @click="openPeriodBoletosModal(item)" v-b-tooltip.righttop="'Gerar boletos de todo o período'">
              <i class="la la-calendar-plus-o" style="font-size: 16px"></i>
            </button>

            <button class="btn btn-xs btn-outline-info btn-icon p-0 mr-1" @click="openExecutionHistory(item)"
              v-b-tooltip.righttop="'Histórico de execuções'">
              <i class="la la-history" style="font-size: 16px"></i>
            </button>

            <!-- NFSe -->
            <button v-if="item.nfses.length" class="btn btn-xs btn-outline-primary btn-icon p-0 mr-1"
              @click="openNfseModal(item)" v-b-tooltip.righttop="'Ver NFS-e do contrato'">
              <i class="la la-file-invoice-dollar" style="font-size: 16px"></i>
            </button>

            <!-- NOVO: Boletos diretos (quando não tem NFSe, ou mesmo tendo) -->
            <button v-if="Array.isArray(item.boletos) && item.boletos.length"
              class="btn btn-xs btn-outline-secondary btn-icon p-0 mr-1" @click="openBoletosModal(item)"
              v-b-tooltip.righttop="'Ver Boletos do contrato'">
              <i class="la la-barcode" style="font-size: 16px"></i>
            </button>

            <button v-if="item.emitir_nfse || item.emitir_boletos ||
              (Array.isArray(item.nfses) && item.nfses.length) ||
              (Array.isArray(item.boletos) && item.boletos.length)"
              class="btn btn-xs btn-outline-success btn-icon p-0 mr-1" @click="openInvoiceEmailModal(item)"
              v-b-tooltip.righttop="'Enviar documentos da fatura por e-mail'">
              <i class="la la-envelope" style="font-size: 16px"></i>
            </button>

            <button @click="editarContrato(item)" v-if="[1, 2].includes(Number(item.status))"
              class="btn btn-xs btn-info btn-icon p-0 mr-1"
              v-b-tooltip.righttop="'Editar Contrato'">
              <Pencil :size="10" />
            </button>
            <button v-if="item.status === 1" @click="excluir(item)" class="btn btn-xs btn-danger btn-icon p-0 mr-1"
              v-b-tooltip.righttop="'Excluir Contrato'">
              <TrashCanIcon :size="16" />
            </button>
            <button class="btn btn-xs btn-outline-dark btn-icon p-0 mr-1" @click="openContratoModal(item)"
              v-b-tooltip.righttop="'Ver contrato'">
              <i class="la la-eye" style="font-size: 16px"></i>
            </button>
          </template>
        </b-table>

        <b-modal id="contrato-view-modal" :title="contratoModalTitle" size="lg" centered hide-footer
          @hidden="resetContratoModal">
          <div v-if="modalContrato.loading" class="p-3">
            <b-skeleton height="22px" class="mb-2" />
            <b-skeleton height="22px" class="mb-2" />
            <b-skeleton height="22px" class="mb-2" />
          </div>

          <div v-else-if="!modalContrato.data" class="text-muted text-center py-4">
            Não foi possível carregar este contrato.
          </div>

          <div v-else class="px-2 py-2">
            <!-- Cabeçalho -->
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div class="h5 mb-1">
                  #{{ modalContrato.data.numero }}
                  <small class="text-muted ml-2" v-if="currentEmpresaId == 1">
                    ({{ planoDict[modalContrato.data.id_plano] || "—" }})
                  </small>
                </div>
                <div class="text-muted">
                  {{ modalContrato.data.cliente.nome }} —
                  {{ maskDoc(modalContrato.data.cnpj_cpf) }}
                </div>
              </div>

              <b-badge :variant="statusVariant(modalContrato.data.status)">
                {{ mapStatus(modalContrato.data.status) }}
              </b-badge>
            </div>

            <!-- Grid de infos principais -->
            <div class="row">
              <div class="col-md-4 mb-3">
                <small class="text-muted d-block">Data da venda</small>
                <div class="font-weight-600">
                  {{ formatDate(modalContrato.data.data_venda) }}
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <small class="text-muted d-block">Vigência</small>
                <div class="font-weight-600">
                  {{ formatDate(modalContrato.data.data_inicio) }} —
                  {{ formatDate(modalContrato.data.data_termino) }}
                </div>
                <small :class="diasClasse(modalContrato.data.__dias)">
                  {{ badgeVigencia(modalContrato.data.__dias) }}
                </small>
              </div>

              <div class="col-md-4 mb-3">
                <small class="text-muted d-block">Dia de cobrança</small>
                <div class="font-weight-600">
                  Dia {{ modalContrato.data.dia_cobranca || "-" }}
                </div>
              </div>

              <div v-if="currentEmpresaId !== 1" class="col-md-4 mb-3">
                <small class="text-muted d-block">Dia de envio da fatura</small>
                <div class="font-weight-600">
                  Dia {{ modalContrato.data.dia_envio_fatura || 1 }}
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <small class="text-muted d-block">Valor total</small>
                <div class="h6 mb-0">
                  {{ formatMoney(modalContrato.data.valor_total) }}
                </div>
              </div>

              <div v-if="isNuts" class="col-md-4 mb-3">
                <small class="text-muted d-block">Taxa de implantação</small>
                <div class="h6 mb-0 d-flex align-items-center">
                  <span>
                    {{
                      modalContrato.data.taxa_implantacao &&
                        Number(modalContrato.data.taxa_implantacao) > 0
                        ? formatMoney(modalContrato.data.taxa_implantacao)
                        : "—"
                    }}
                  </span>

                  <i class="la ml-1" :class="modalContrato.data.gerar_boleto_taxa
                    ? 'la-receipt text-success'
                    : 'la-ban text-muted'
                    " v-b-tooltip.hover :title="taxaTooltip(modalContrato.data)" />
                </div>
              </div>

              <div class="col-md-4 mb-3" v-if="modalContrato.data.plugin_certificado">
                <small class="text-muted d-block">Certificado Digital</small>
                <div class="h6 mb-0">
                  {{ formatMoney(modalContrato.data.valor_certificado || 0) }}
                </div>
              </div>

              <div class="col-md-4 mb-3" v-if="modalContrato.data.plugin_tef">
                <small class="text-muted d-block">TEF Integrado</small>
                <div class="h6 mb-0">
                  {{ formatMoney(modalContrato.data.valor_tef || 0) }}
                </div>
              </div>
            </div>

            <!-- Flags de cobrança -->
            <div class="mb-3">
              <b-badge :variant="modalContrato.data.emitir_nfse ? 'success' : 'secondary'" pill class="mr-2">
                NFSe
              </b-badge>

              <b-badge :variant="modalContrato.data.emitir_boletos ? 'primary' : 'secondary'" pill class="mr-2">
                Boleto
              </b-badge>

              <b-badge v-if="modalContrato.data.cobranca_anual" variant="warning" pill class="mr-2">
                Cobrança Anual
              </b-badge>

              <b-badge v-if="modalContrato.data.carne_asaas" variant="success" pill class="mr-2">
                <a :href="modalContrato.data.carne_asaas" target="_blank" rel="noopener noreferrer" class="text-white"
                  style="text-decoration: none">
                  Carnê
                </a>
              </b-badge>

              <b-badge v-if="modalContrato.data.link_fatura" variant="info" pill class="mr-2">
                <a :href="modalContrato.data.link_fatura" target="_blank" rel="noopener noreferrer" class="text-white"
                  style="text-decoration: none">
                  Fatura
                </a>
              </b-badge>

              <b-badge v-if="modalContrato.data.pular_final_semana" variant="warning" pill class="mr-2">
                Pular finais semana
              </b-badge>

              <b-badge v-if="modalContrato.data.plugin_certificado" variant="dark" pill class="mr-2">
                Certificado
              </b-badge>

              <b-badge v-if="modalContrato.data.plugin_tef" variant="dark" pill class="mr-2">
                TEF
              </b-badge>
            </div>

            <!-- Observação -->
            <div v-if="modalContrato.data.observacao" class="border rounded p-3 mb-4 bg-light">
              <small class="text-muted d-block mb-1">Observação</small>
              <div class="mb-0">
                {{ modalContrato.data.observacao }}
              </div>
            </div>

            <div v-if="pagamentosContrato.length" class="mb-4">
              <small class="text-muted d-block mb-2">Forma de pagamento</small>

              <div v-for="(pagamento, index) in pagamentosContrato" :key="index" class="border rounded p-3 mb-3">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <small class="text-muted d-block">Descrição</small>
                    <div class="font-weight-600">
                      {{ pagamento.descricao || "—" }}
                    </div>
                  </div>

                  <div class="col-md-2 mb-3">
                    <small class="text-muted d-block">Parcelas</small>
                    <div class="font-weight-600">
                      {{ pagamento.vezes || "—" }}
                    </div>
                  </div>

                  <div class="col-md-3 mb-3">
                    <small class="text-muted d-block">Periodicidade</small>
                    <div class="font-weight-600">
                      {{
                        getPeriodicidadePagamento(
                          pagamento.intervalo_dias || pagamento.intervalo_dias_collection
                      )
                      }}
                    </div>
                  </div>
                </div>

                <div v-if="Array.isArray(pagamento.range) && pagamento.range.length" class="mt-2">
                  <small class="text-muted d-block mb-2">Parcelas previstas</small>

                  <b-table :items="pagamento.range.map((item, i) => ({ ...item, id: i + 1 }))"
                    :fields="pagamentosFields" small bordered responsive head-variant="light">
                    <template #cell(id)="{ item }">
                      {{ item.id }}
                    </template>

                    <template #cell(date)="{ item }">
                      {{ formatDate(item.date) }}
                    </template>

                    <template #cell(value)="{ item }">
                      {{ formatMoney(item.value) }}
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div>
        </b-modal>

        <b-modal id="execution-history-modal" :title="executionHistoryTitle" size="lg" centered hide-footer
          @hidden="resetExecutionHistory">
          <div v-if="executionHistory.loading" class="p-3">
            <b-skeleton height="28px" class="mb-2" />
            <b-skeleton height="28px" class="mb-2" />
            <b-skeleton height="28px" class="mb-2" />
          </div>

          <b-table v-else :items="executionHistory.items" :fields="executionHistoryFields" small responsive
            head-variant="light" show-empty empty-text="Nenhuma execução registrada para este contrato.">
            <template #cell(executado_em)="{ item }">
              {{ formatDateTime(item.executado_em) }}
            </template>
            <template #cell(competencia)="{ item }">
              {{ formatCompetencia(item.competencia) }}
            </template>
            <template #cell(origem)="{ item }">
              {{ item.origem === "manual" ? "Manual" : "Automática" }}
            </template>
            <template #cell(status)="{ item }">
              <b-badge :variant="executionStatusVariant(item.status)">
                {{ executionStatusLabel(item.status) }}
              </b-badge>
            </template>
            <template #cell(valor)="{ item }">
              {{ formatMoney(item.valor) }}
            </template>
            <template #cell(usuario)="{ item }">
              {{ item.usuario?.nome || "Sistema" }}
            </template>
            <template #cell(mensagem)="{ item }">
              <span :title="item.mensagem">{{ item.mensagem || "—" }}</span>
            </template>
          </b-table>
        </b-modal>

        <b-modal id="modal-logs-renovacao" title="Histórico de Renovações pelo Cliente" size="lg" hide-footer>
          <b-table :items="tabelaLogs" :fields="camposLogs" small bordered hover head-variant="light"
            sticky-header="50vh" show-empty empty-text="Sem registros de renovação.">
            <template #cell(ano_referencia)="{ item }">
              <span class="font-weight-bold">{{ item.ano_referencia }}</span>
            </template>

            <template #cell(data_renovacao)="{ item }">
              <span class="text-muted">{{
                formatDate(item.data_renovacao)
                }}</span>
            </template>

            <template #cell(canal)="{ item }">
              <b-badge v-if="item.canal" variant="info">{{
                item.canal
                }}</b-badge>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(usuario_nome)="{ item }">
              {{ item.usuario_nome || "—" }}
            </template>

            <template #cell(usuario_email)="{ item }">
              <a v-if="item.usuario_email" :href="`mailto:${item.usuario_email}`">{{ item.usuario_email }}</a>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>
        </b-modal>

        <!-- Modal: Lista de NFSe do contrato -->
        <b-modal id="nfse-list-modal" :title="modalNfseTitle" size="lg" centered hide-footer @hidden="resetNfseModal">
          <div v-if="modalNfse.loading" class="p-3">
            <b-skeleton height="28px" class="mb-2" />
            <b-skeleton height="28px" class="mb-2" />
            <b-skeleton height="28px" class="mb-2" />
          </div>

          <div v-else>
            <div v-if="!modalNfse.notas.length" class="text-muted text-center py-4">
              Nenhuma NFSe encontrada para este contrato.
            </div>

            <b-table v-else :items="modalNfse.notas" :fields="nfseFields" small responsive head-variant="light">
              <template #cell(total_nfse)="{ item }">
                {{ formatMoney(item.total_nfse) }}
              </template>
              <template #cell(emissao)="{ item }">
                {{ formatDate(item.emissao) }}
              </template>
              <template #cell(acoes)="{ item }">
                <div class="d-flex flex-wrap justify-content-end">
                  <!-- Imprimir NFSe -->
                  <b-button size="sm" variant="outline-primary" class="mr-2 mb-1" :disabled="printingId === item.id_nfse ||
                    (!item.nfse_url && loadingPrint)
                    " @click="handleImprimirNFSe(item)">
                    <b-spinner v-if="printingId === item.id_nfse" small class="mr-2" />
                    <i v-else class="la la-print mr-1"></i>
                    {{ printingId === item.id_nfse ? "Abrindo..." : "NFSe" }}
                  </b-button>

                  <!-- Boletos (sempre tratar como array) -->
                  <template v-if="Array.isArray(item.boletos) && item.boletos.length">
                    <b-button v-for="(b, idx) in item.boletos" :key="b.id ?? idx" size="sm" variant="outline-secondary"
                      class="mr-2 mb-1" :disabled="!b.url" @click="abrirBoleto(b)">
                      <i class="la la-barcode mr-1"></i>
                      {{ b.doc }} —
                      {{ b.vencimento }}
                    </b-button>
                  </template>

                  <b-badge v-else variant="light" class="mb-1">Sem boletos</b-badge>
                </div>
              </template>
            </b-table>
          </div>
        </b-modal>

        <!-- NOVO: Modal - Lista de Boletos (para contratos sem NFSe) -->
        <b-modal id="boletos-list-modal" :title="boletosModalTitle" size="lg" centered hide-footer
          @hidden="resetBoletosModal">
          <div v-if="modalBoletos.loading" class="p-3">
            <b-skeleton height="28px" class="mb-2" />
            <b-skeleton height="28px" class="mb-2" />
            <b-skeleton height="28px" class="mb-2" />
          </div>

          <div v-else>
            <div v-if="
              !Array.isArray(modalBoletos.itens) || !modalBoletos.itens.length
            " class="text-muted text-center py-4">
              Nenhum boleto encontrado para este contrato.
            </div>

            <b-table v-else :items="modalBoletos.itens" :fields="boletosFields" small responsive head-variant="light">
              <template #cell(valor)="{ item }">
                {{ formatMoney(item.valor) }}
              </template>
              <template #cell(recebido)="{ item }">
                <b-badge :variant="item.recebido ? 'success' : 'secondary'">
                  {{ item.recebido ? "Recebido" : "Pendente" }}
                </b-badge>
              </template>
              <template #cell(data_vencimento)="{ item }">
                {{ item.vencimento }}
              </template>
              <template #cell(acoes)="{ item }">
                <div class="d-flex justify-content-end">
                  <b-button size="sm" variant="outline-secondary" class="mr-2" :disabled="!item.url"
                    @click="abrirBoleto(item)">
                    <i class="la la-barcode mr-1"></i> Abrir
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
        </b-modal>

        <!-- Modal: Boleto da Taxa de Implantação -->
        <b-modal v-if="isNuts" id="taxa-boleto-modal" :title="taxaModalTitle" size="md" centered hide-footer
          @hidden="resetTaxaModal">
          <div v-if="modalTaxa.loading" class="p-3">
            <b-skeleton height="24px" class="mb-2" />
            <b-skeleton height="24px" class="mb-2" />
            <b-skeleton height="24px" class="mb-2" />
          </div>

          <div v-else>
            <div v-if="!modalTaxa.boleto" class="text-muted text-center py-4">
              Nenhum boleto de taxa encontrado para este contrato.
            </div>

            <div v-else class="px-2 py-2">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="font-weight-600">Taxa de implantação</div>
                <b-badge :variant="modalTaxa.boleto.recebido ? 'success' : 'secondary'">
                  {{ modalTaxa.boleto.recebido ? "Recebido" : "Pendente" }}
                </b-badge>
              </div>

              <div class="mb-1">
                <small class="text-muted d-block">Valor</small>
                <div class="h5 mb-0">
                  {{ formatMoney(modalTaxa.boleto.valor) }}
                </div>
              </div>

              <div class="mb-3">
                <small class="text-muted d-block">Vencimento</small>
                <div class="h6 mb-0">
                  {{ modalTaxa.boleto.data_vencimento }}
                </div>
              </div>

              <div class="d-flex">
                <b-button :disabled="!modalTaxa.boleto.url_boleto" variant="primary" size="sm" class="mr-2"
                  @click="abrirBoleto({ url: modalTaxa.boleto.url_boleto })">
                  <i class="la la-barcode mr-2"></i> Abrir boleto
                </b-button>

                <b-button v-if="modalTaxa.boleto.carnet_url" variant="outline-secondary" size="sm" @click="
                  window.open(
                    modalTaxa.boleto.carnet_url,
                    '_blank',
                    'noopener'
                  )
                  ">
                  <i class="la la-file-pdf-o mr-2"></i> Carnê
                </b-button>
              </div>
            </div>
          </div>
        </b-modal>

        <!-- Paginação -->
        <div class="d-flex justify-content-between align-items-center px-4 py-3">
          <small class="text-muted">Total: {{ pagination.total }}</small>
          <div class="d-flex align-items-center">
            <b-form-select v-model="perPage" :options="[10, 20, 50, 100]" size="sm" class="mr-3"
              @change="changePerPage" />
            <b-pagination v-model="page" :total-rows="pagination.total" :per-page="perPage" size="sm"
              @input="fetchData" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import TrashCanIcon from "vue-material-design-icons/TrashCan.vue";
import Pencil from "vue-material-design-icons/Pencil.vue";
import { SistemaMixin } from "@/modules/Application";
import swal from "sweetalert";
import { mapGetters, mapState } from "vuex";

export default {
  name: "ContratosIndex",
  mixins: [SistemaMixin],
  components: {
    Pencil,
    TrashCanIcon,
  },
  data() {
    return {
      loadingKpis: false,
      loading: false,
      rows: [],
      page: 1,
      perPage: 20,
      pagination: { total: 0, last_page: 1 },
      sort: { field: null, type: null, typeDesc: false },
      monthRange: { start: null, end: null }, // cache intervalo do mês
      statusOptions: [
        { value: null, text: "Todos" },
        { value: 1, text: "Editando" },
        { value: 2, text: "Ativa" },
        { value: 3, text: "Cancelado" },
        { value: 4, text: "Vencido" },
        { value: 6, text: "Congelado" },
      ],
      fields: [
        { key: "select", label: "", thStyle: { width: "36px" } },
        { key: "contrato", label: "Contrato", sortable: true },
        { key: "cliente", label: "Cliente", sortable: false, thClass: "w-25" },
        { key: "venda", label: "Dt.Venda", sortable: true },
        { key: "vigencia", label: "Vigência", sortable: true },
        { key: "valor", label: "Valor", sortable: false, class: "text-right" },
        { key: "status", label: "Status", sortable: false },
        { key: "cobranca", label: "Cobrança" },
        { key: "acoes", label: "Ações", class: "text-right" },
      ],
      hasSort: false,
      firstLoad: true,
      searchTerm: "",
      selectedIds: [], // Array reativo
      kpis: {
        ativos: 0,
        aVencer30: 0,
        vencidos: 0,
        valorMensal: 0,
        totalJanela: 0,
        janela: null,
      },
      confirmLoading: false,
      executeLoading: false,
      executionTarget: null,
      periodBoletos: {
        contrato: null,
        loading: false,
      },
      executionHistory: {
        loading: false,
        contrato: null,
        items: [],
      },
      invoiceEmail: {
        contrato: null,
        email: "",
        competencia: "",
        nfses: [],
        boletos: [],
        hasDocuments: false,
        loadError: "",
        loading: false,
        sending: false,
      },
      executionHistoryFields: [
        { key: "executado_em", label: "Data" },
        { key: "competencia", label: "Competência" },
        { key: "origem", label: "Origem" },
        { key: "status", label: "Status" },
        { key: "valor", label: "Valor", class: "text-right" },
        { key: "usuario", label: "Usuário" },
        { key: "mensagem", label: "Resultado" },
      ],
      modalNfse: {
        loading: false,
        contrato: null,
        notas: [],
      },
      nfseFields: [
        { key: "numero", label: "Número", tdClass: "align-middle" },
        { key: "emissao", label: "Emissão", tdClass: "align-middle" },
        { key: "total_nfse", label: "Valor", tdClass: "align-middle" },
        {
          key: "acoes",
          label: "Ações",
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],
      // NOVO: modal de boletos diretos
      modalBoletos: {
        loading: false,
        contrato: null,
        itens: [], // [{ id, doc, valor, vencimento, url }]
      },
      boletosFields: [
        { key: "doc", label: "Documento", tdClass: "align-middle" },
        { key: "valor", label: "Valor", tdClass: "align-middle" },
        { key: "recebido", label: "Status", tdClass: "align-middle" },
        { key: "vencimento", label: "Vencimento", tdClass: "align-middle" },
        {
          key: "acoes",
          label: "Ações",
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],
      printingId: null,
      loadingPrint: false,
      modalTaxa: {
        loading: false,
        contrato: null,
        boleto: null,
      },
      diaCobrancaForm: { day: null },
      diaCobrancaLoading: false,
      diaEnvioFaturaForm: { day: null },
      diaEnvioFaturaLoading: false,
      cancelLoading: false,
      licencaForm: {
        plano: null,
      },
      planoOptions: [
        { value: 7, text: "Basic" },
        { value: 2, text: "Essencial Anual" },
        { value: 3, text: "Essencial Mensal" },
        { value: 4, text: "Standard Anual" },
        { value: 6, text: "Standard Mensal" },
      ],
      filters: { cliente: "", status: null, dateFrom: null, dateTo: null },
      logsRenovacao: [],
      camposLogs: [
        { key: "ano_referencia", label: "Ano", sortable: true },
        { key: "data_renovacao", label: "Data", sortable: true },
        { key: "canal", label: "Canal", sortable: true },
        { key: "usuario_nome", label: "Usuário", sortable: true },
        { key: "usuario_email", label: "E-mail", sortable: false },
      ],
      modalContrato: {
        loading: false,
        data: null, // objeto normalizado do contrato
      },
      pagamentosFields: [
        { key: "id", label: "#" },
        { key: "date", label: "Vencimento" },
        { key: "value", label: "Valor" },
      ]
    };
  },
  created() {
    const start = this.getMonthStart();
    const end = this.getMonthEnd();
    this.monthRange = { start, end };

    this.fetchKPIs();
    this.fetchData();
    this.debouncedFetch = debounce(this.fetchData, 450);
    this.filters.dateFrom = this.getMonthStart();
    this.filters.dateTo = this.getMonthEnd();
  },
  watch: {
    "filters.cliente"() {
      this.debouncedFetch();
    },
    "filters.status"() {
      this.page = 1;
      this.fetchData();
    },
    searchTerm() {
      this.debouncedFetch();
    },
    "filters.dateFrom"() {
      this.fetchKPIs();
      this.page = 1;
      this.fetchData();
    },
    "filters.dateTo"() {
      this.page = 1;
      this.fetchKPIs();
      this.fetchData();
    },
  },
  computed: {
    ...mapGetters(["isNuts"]),
    contratoModalTitle() {
      const num = this.modalContrato?.data?.numero
        ? `#${this.modalContrato.data.numero}`
        : "";
      const cli = this.modalContrato?.data?.cliente.nome || "";
      return `Contrato ${num} ${cli ? "— " + cli : ""}`;
    },
    tabelaLogs() {
      return (this.logsRenovacao || []).map((l) => ({
        ano_referencia: l.ano_referencia,
        data_renovacao: l.data_renovacao,
        canal: l.canal,
        usuario_nome: l?.usuario?.nome || null,
        usuario_email: l?.usuario?.email || l?.usuario?.usuario || null,
      }));
    },
    totalPeriodoLabel() {
      const j = this.kpis?.janela;
      if (!j || !j.from || !j.to) return "Período atual";
      const fmt = (s) =>
        new Date(s + "T00:00:00").toLocaleDateString("pt-BR", {
          timeZone: "America/Sao_Paulo",
        });
      return `De ${fmt(j.from)} a ${fmt(j.to)}`;
    },
    aVencerLabel() {
      if (!this.filters?.dateTo) return "A vencer (30 dias)";
      const s = new Date(this.filters.dateTo + "T00:00:00").toLocaleDateString(
        "pt-BR",
        { timeZone: "America/Sao_Paulo" }
      );
      return `A vencer (até ${s})`;
    },
    periodLabel() {
      const f = (s) =>
        !s
          ? ""
          : new Date(s + "T00:00:00").toLocaleDateString("pt-BR", {
            timeZone: "America/Sao_Paulo",
          });
      const a = this.filters.dateFrom ? f(this.filters.dateFrom) : "—";
      const b = this.filters.dateTo ? f(this.filters.dateTo) : "—";
      return `Filtrando: ${a} — ${b}`;
    },
    planoDict() {
      return this.planoOptions.reduce((acc, opt) => {
        acc[opt.value] = opt.text;
        return acc;
      }, {});
    },
    firstSelected() {
      if (!this.selectedIds.length) return null;
      return (
        this.rows.find((r) => r.id_contrato_fatura === this.selectedIds[0]) ||
        null
      );
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    isInvoiceEmailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.invoiceEmail.email || "");
    },
    canActivateSelected() {
      if (!this.selectedIds.length) return false;

      const selecionados = this.rows.filter((row) =>
        this.selectedIds.includes(row.id_contrato_fatura)
      );

      return (
        selecionados.length === this.selectedIds.length &&
        selecionados.every((row) => Number(row.status) !== 2)
      );
    },
    canExecuteSelected() {
      return this.selectedCount === 1 && this.canExecuteContract(this.firstSelected);
    },
    executionHistoryTitle() {
      const contrato = this.executionHistory.contrato;
      if (!contrato) return "Histórico de execuções";
      return `Histórico do contrato #${contrato.numero} — ${contrato.cliente}`;
    },
    allSelectedOnPage() {
      if (!this.rows.length) return false;
      return this.rows.every(
        (r) =>
          this.selectedIds.includes(r.id_contrato_fatura) ||
          this.isRowDisabled(r)
      );
    },
    someSelectedOnPage() {
      const selectable = this.rows.filter((r) => !this.isRowDisabled(r));
      if (!selectable.length) return false;
      const count = selectable.filter((r) =>
        this.selectedIds.includes(r.id_contrato_fatura)
      ).length;
      return count > 0 && count < selectable.length;
    },
    modalNfseTitle() {
      const num = this.modalNfse.contrato?.numero
        ? `#${this.modalNfse.contrato.numero}`
        : "";
      const cli = this.modalNfse.contrato?.cliente || "";
      return `NFSe do Contrato ${num} ${cli ? "— " + cli : ""}`;
    },
    boletosModalTitle() {
      const num = this.modalBoletos.contrato?.numero
        ? `#${this.modalBoletos.contrato.numero}`
        : "";
      const cli = this.modalBoletos.contrato?.cliente || "";
      return `Boletos do Contrato ${num} ${cli ? "— " + cli : ""}`;
    },
    taxaModalTitle() {
      const num = this.modalTaxa.contrato?.numero
        ? `#${this.modalTaxa.contrato.numero}`
        : "";
      const cli = this.modalTaxa.contrato?.cliente || "";
      return `Boleto da taxa ${num} ${cli ? "— " + cli : ""}`;
    },
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
    dayOptions() {
      return Array.from({ length: 31 }, (_, i) => ({
        value: i + 1,
        text: String(i + 1),
      }));
    },
    currentEmpresaId() {
      return Number(this.currentUser?.usuario_empresa?.id_empresa ?? 0);
    },
    currentMonthLabel() {
      const now = new Date();
      const nomeMes = now.toLocaleString("pt-BR", { month: "short" });
      return `${nomeMes.charAt(0).toUpperCase() + nomeMes.slice(1)
        } ${now.getFullYear()}`;
    },
    currentCompetenciaLabel() {
      const now = new Date();
      return `${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
    },
    pagamentosContrato() {
      const pagamentos =
        this.modalContrato?.data?.__pagamentos ??
        this.modalContrato?.data?.pagamentos ??
        [];

      if (Array.isArray(pagamentos)) {
        return pagamentos;
      }

      if (typeof pagamentos === "string") {
        try {
          const parsed = JSON.parse(pagamentos);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          return [];
        }
      }

      if (pagamentos && typeof pagamentos === "object") {
        return [pagamentos];
      }

      return [];
    },
  },
  methods: {
    canGeneratePeriodBoletos(contrato) {
      return Number(contrato?.status) === 2 &&
        Boolean(contrato?.emitir_boletos) &&
        Number(contrato?.cobranca_anual) !== 1 &&
        Boolean(contrato?.data_inicio) &&
        Boolean(contrato?.data_termino);
    },
    openPeriodBoletosModal(contrato) {
      this.periodBoletos.contrato = contrato;
      this.$bvModal.show("generate-period-boletos-modal");
    },
    async handleGeneratePeriodBoletos() {
      if (!this.periodBoletos.contrato || this.periodBoletos.loading) return;

      this.periodBoletos.loading = true;
      try {
        const { data } = await this.$http.post("/contrato-fatura/gerar-boletos-periodo", {
          id_contrato_fatura: this.periodBoletos.contrato.id_contrato_fatura,
        });
        const resumo = data?.data || {};
        const detalhes = `${resumo.boletos_gerados || 0} novo(s), ${resumo.boletos_existentes || 0} já existente(s).`;

        if (data.status) {
          this.$toast.success(`${data.message} ${detalhes}`);
        } else {
          this.$toast.warning(`${data.message} ${detalhes}`);
        }

        this.$bvModal.hide("generate-period-boletos-modal");
        this.fetchData();
      } catch (error) {
        const message = error?.response?.data?.message || "Não foi possível gerar os boletos do período.";
        this.$toast.error(message);
      } finally {
        this.periodBoletos.loading = false;
      }
    },
    resetPeriodBoletosModal() {
      if (!this.periodBoletos.loading) {
        this.periodBoletos.contrato = null;
      }
    },
    canExecuteContract(contrato) {
      return Number(contrato?.status) === 2 && Number(contrato?.cobranca_anual) !== 1;
    },
    openExecuteModal(contrato) {
      if (!this.canExecuteContract(contrato)) {
        this.$toast.warning("Somente contratos mensais ativos podem ser executados agora.");
        return;
      }

      this.executionTarget = contrato;
      this.$bvModal.show("confirm-execute-contract");
    },
    async handleExecuteNow() {
      if (!this.executionTarget || this.executeLoading) return;

      this.executeLoading = true;
      try {
        const { data } = await this.$http.post("/contrato-fatura/executar-agora", {
          id_contrato_fatura: this.executionTarget.id_contrato_fatura,
        });

        if (["ok", "processando"].includes(data.status)) {
          this.$toast.success(data.message || "Contrato executado com sucesso!");
        } else {
          await this.$swal.fire({
            icon: "info",
            title: "Contrato já executado",
            text: data.message || "Já existe processamento para a competência vigente.",
            confirmButtonText: "Entendi",
          });
        }

        this.$bvModal.hide("confirm-execute-contract");
        this.clearSelection();
        this.fetchData();
      } catch (error) {
        const message = error?.response?.data?.message || "Não foi possível executar o contrato.";
        this.$toast.error(message);
      } finally {
        this.executeLoading = false;
      }
    },
    resetExecuteModal() {
      if (!this.executeLoading) this.executionTarget = null;
    },
    async openExecutionHistory(contrato) {
      this.executionHistory = { loading: true, contrato, items: [] };
      this.$bvModal.show("execution-history-modal");

      try {
        const { data } = await this.$http.post("/contrato-fatura/historico-execucoes", {
          id_contrato_fatura: contrato.id_contrato_fatura,
        });
        this.executionHistory.items = Array.isArray(data?.history) ? data.history : [];
      } catch (error) {
        this.$toast.error("Não foi possível carregar o histórico de execuções.");
      } finally {
        this.executionHistory.loading = false;
      }
    },
    resetExecutionHistory() {
      this.executionHistory = { loading: false, contrato: null, items: [] };
    },
    executionStatusLabel(status) {
      return {
        processando: "Processando",
        ok: "Concluído",
        skip: "Ignorado",
        fail: "Falhou",
      }[status] || status;
    },
    executionStatusVariant(status) {
      return {
        processando: "warning",
        ok: "success",
        skip: "secondary",
        fail: "danger",
      }[status] || "secondary";
    },
    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleString("pt-BR");
    },
    formatCompetencia(value) {
      if (!value) return "—";
      const raw = String(value).slice(0, 10);
      const parts = raw.split("-");
      return parts.length >= 2 ? `${parts[1]}/${parts[0]}` : raw;
    },
    getPeriodicidadePagamento(intervaloDias) {
      if (!intervaloDias || !intervaloDias.intervalo) return "—";

      const intervalo = intervaloDias.intervalo;
      const qtd = intervalo.periodo_qtd || 0;
      const tipo = intervalo.periodo_descricao || "";

      if (tipo === "months") {
        return qtd === 1 ? "Mensal" : `A cada ${qtd} meses`;
      }

      if (tipo === "days") {
        return qtd === 1 ? "Diária" : `A cada ${qtd} dias`;
      }

      if (tipo === "years") {
        return qtd === 1 ? "Anual" : `A cada ${qtd} anos`;
      }

      return `${qtd} ${tipo}`;
    },

    getMeioPagamentoLabel(id) {
      const mapa = {
        15: "Boleto",
        3: "PIX",
        4: "Cartão",
        1: "Dinheiro",
      };

      return mapa[id] || `ID ${id}`;
    },
    openContratoModal(item) {
      // Preload com os dados da linha para abrir instantâneo
      const preload = this.normalizeContrato(item);
      this.modalContrato.data = preload;
      this.modalContrato.loading = true;
      this.$bvModal.show("contrato-view-modal");

      // (Opcional) Busca detalhada no backend para enriquecer
      // Se você já tem tudo na linha, pode remover o fetch abaixo.
      this.$http
        .post("/contrato-fatura/find", {
          id_contrato_fatura: item.id_contrato_fatura,
        })
        .then(({ data }) => {
          const full = this.normalizeContrato({ ...preload, ...data });
          this.modalContrato.data = full;
        })
        .catch(() => {
          // mantém preload; já estamos mostrando algo
          this.$toast?.warning?.(
            "Não foi possível carregar detalhes completos. Mostrando dados parciais."
          );
        })
        .finally(() => {
          this.modalContrato.loading = false;
        });
    },

    normalizeContrato(c) {
      if (!c) return null;
      const dias = this.diffInDays(c.data_termino);
      return {
        ...c,
        __dias: dias,
        __servicos: this.safeJSON(c.servicos),
        __pagamentos: this.safeJSON(c.pagamentos),
        // normalizações úteis:
        id_plano: c.id_plano ?? null,
        link_fatura: c.link_fatura ?? null,
        carne_asaas: c.carne_asaas ?? null,
      };
    },

    resetContratoModal() {
      this.modalContrato = { loading: false, data: null };
    },
    formatDateString(d) {
      if (!d) return "—";

      // Se vier como 'YYYY-MM-DD' (string pura do back)
      if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split("-");
        return `${day}/${m}/${y}`;
      }

      // Se vier Date ou outra string parseável
      const dt = d instanceof Date ? d : new Date(d);
      if (isNaN(dt)) return "—";
      return dt.toLocaleDateString("pt-BR");
    },
    openAtivarLicencaModal() {
      if (!this.firstSelected) {
        this.$toast?.info?.("Selecione um contrato.");
        return;
      }
      this.licencaForm.plano = this.firstSelected.id_plano || null;
      this.$bvModal.show("confirm-ativar-licenca");
    },

    async handleConfirmAtivarLicenca() {
      this.confirmLoading = true;
      try {
        if (!this.firstSelected) return;

        await this.$http.post("/contrato-fatura/licenca", {
          cnpj_cpf: this.firstSelected.cnpj_cpf,
          plano: this.licencaForm.plano,
        });

        this.$toast.success("Licença ativada por mais 1 ano!");
        this.clearSelection();
        this.fetchKPIs();
        this.fetchData();

        this.$bvModal.hide("confirm-ativar-licenca");
      } catch (e) {
        this.$toast.error("Erro ao ativar licença.");
      } finally {
        this.confirmLoading = false;
      }
    },

    async openTaxaModal(contrato) {
      this.modalTaxa.contrato = contrato;
      this.modalTaxa.loading = true;
      this.modalTaxa.boleto = null;
      try {
        if (contrato.taxa) {
          this.modalTaxa.boleto = this.normalizeTaxaBoleto({
            valor: Number(contrato.taxa_implantacao || 0),
            data_vencimento:
              this.formatDateString(contrato.taxa.data_vencimento) || null,
            url_boleto: contrato.taxa.url_boleto,
            status_boleto: contrato.taxa.recebido || null,
          });
          return;
        }
      } catch (e) {
        this.$toast?.danger?.("Não foi possível carregar o boleto da taxa.");
      } finally {
        this.modalTaxa.loading = false;
        this.$bvModal.show("taxa-boleto-modal");
      }
    },
    abrirLogs(contrato) {
      this.logsRenovacao = (contrato.renovacoes_log || []).map((r) => ({
        id_renovacao_log: r.id_renovacao_log,
        id_contrato_fatura: r.id_contrato_fatura,
        ano_referencia: r.ano_referencia,
        renovado_pelo_cliente: r.renovado_pelo_cliente,
        data_renovacao: r.data_renovacao,
        valor: r.valor,
        id_usuario: r.id_usuario,
        observacao: r.observacao,
        canal: r.canal,
        usuario: r.usuario
          ? {
            id_usuario: r.usuario.id_usuario,
            nome: r.usuario.nome,
            usuario: r.usuario.email || r.usuario.usuario, // accessor se existir
          }
          : null,
      }));

      this.$bvModal.show("modal-logs-renovacao");
    },

    normalizeTaxaBoleto(b) {
      return {
        valor: Number(b?.valor ?? 0),
        data_vencimento: b?.data_vencimento ?? null,
        url_boleto: b?.url_boleto ?? null,
        status_boleto: b?.status_boleto ?? null,
      };
    },

    resetTaxaModal() {
      this.modalTaxa = { loading: false, contrato: null, boleto: null };
    },

    async openNfseModal(contrato) {
      this.modalNfse.contrato = contrato;
      this.modalNfse.loading = true;
      this.modalNfse.notas = [];

      const preload = Array.isArray(contrato.nfses) ? contrato.nfses : null;

      try {
        if (preload && preload.length) {
          this.modalNfse.notas = preload.map((n) => this.normalizeNfse(n));
          this.modalNfse.loading = false;
          this.$bvModal.show("nfse-list-modal");
          return;
        }

        const { data } = await this.$http.post("/contrato-fatura/nfses", {
          id_contrato_fatura: contrato.id_contrato_fatura,
        });

        const list = Array.isArray(data) ? data : data?.nfses || [];
        this.modalNfse.notas = list.map((n) => this.normalizeNfse(n));
      } catch (e) {
        this.$toast?.danger?.("Não foi possível carregar as NFS-e.");
      } finally {
        this.modalNfse.loading = false;
        this.$bvModal.show("nfse-list-modal");
      }
    },
    taxaTooltip(item) {
      const temTaxa = Number(item?.taxa_implantacao || 0) > 0;
      if (!temTaxa) return "Sem taxa de implantação";
      return item?.gerar_boleto_taxa
        ? "Boleto da taxa foi GERADO"
        : "Boleto da taxa NÃO foi gerado";
    },

    // NOVO: abrir modal de boletos diretos (sem NFSe)
    async openBoletosModal(contrato) {
      this.modalBoletos.contrato = contrato;
      this.modalBoletos.loading = true;
      this.modalBoletos.itens = [];

      try {
        // usa eager load se veio no payload
        const preload = Array.isArray(contrato.boletos)
          ? contrato.boletos
          : null;
        if (preload && preload.length) {
          this.modalBoletos.itens = preload.map((b) => this.normalizeBoleto(b));
        } else {
          // fallback: busca no backend caso queira endpoint dedicado
          // const { data } = await this.$http.post('/contrato-fatura/boletos', {
          //   id_contrato_fatura: contrato.id_contrato_fatura
          // });
          // const list = Array.isArray(data) ? data : (data?.boletos || []);
          // this.modalBoletos.itens = list.map((b) => this.normalizeBoleto(b));
        }
      } catch (e) {
        this.$toast?.danger?.("Não foi possível carregar os boletos.");
      } finally {
        this.modalBoletos.loading = false;
        this.$bvModal.show("boletos-list-modal");
      }
    },

    normalizeNfse(n) {
      return {
        id_nfse: n.id_nfse,
        id_contrato_fatura: n.id_contrato_fatura,
        numero: n.numero,
        emissao: n.emissao,
        total_nfse: Number(n.total_nfse ?? n.valor ?? 0),
        boletos: Array.isArray(n.boletos)
          ? n.boletos.map(this.normalizeBoleto)
          : [],
      };
    },

    normalizeBoleto(b) {
      return {
        id: b.id ?? null,
        doc: b.documento ?? "",
        valor: Number(b.valor ?? 0),
        vencimento: this.formatDateString(b.data_vencimento) ?? null,
        url: b.url_boleto ?? null,
        recebido: b.recebido ?? false,
      };
    },

    resetNfseModal() {
      this.modalNfse = { loading: false, contrato: null, notas: [] };
    },

    // NOVO
    resetBoletosModal() {
      this.modalBoletos = { loading: false, contrato: null, itens: [] };
    },
    openInvoiceEmailModal(contrato) {
      this.invoiceEmail = {
        contrato,
        email: contrato.email_fatura || "",
        competencia: this.getMonthStart().slice(0, 7),
        nfses: [],
        boletos: [],
        hasDocuments: false,
        loadError: "",
        loading: false,
        sending: false,
      };
      this.$bvModal.show("send-contract-invoice-modal");
      this.loadInvoiceEmailPreview(true);
    },
    async loadInvoiceEmailPreview(fillContractEmail = false) {
      if (!this.invoiceEmail.contrato || !this.invoiceEmail.competencia) return;

      this.invoiceEmail.loading = true;
      this.invoiceEmail.nfses = [];
      this.invoiceEmail.boletos = [];
      this.invoiceEmail.hasDocuments = false;
      this.invoiceEmail.loadError = "";

      try {
        const { data } = await this.$http.post("/contrato-fatura/preparar-envio-fatura", {
          id_contrato_fatura: this.invoiceEmail.contrato.id_contrato_fatura,
          competencia: this.invoiceEmail.competencia,
        });

        if (fillContractEmail) {
          this.invoiceEmail.email = data.email || this.invoiceEmail.email;
        }
        this.invoiceEmail.nfses = Array.isArray(data.nfses) ? data.nfses : [];
        this.invoiceEmail.boletos = Array.isArray(data.boletos) ? data.boletos : [];
        this.invoiceEmail.hasDocuments = Boolean(data.possui_documentos);
      } catch (error) {
        const status = error?.response?.status;
        const message = status === 401
          ? "Sua sessão expirou. Entre novamente no sistema para consultar os documentos."
          : error?.response?.data?.message || "Não foi possível consultar os documentos da competência.";
        this.invoiceEmail.loadError = message;
        this.$toast.error(message);
      } finally {
        this.invoiceEmail.loading = false;
      }
    },
    async handleSendInvoiceEmail() {
      if (!this.isInvoiceEmailValid || !this.invoiceEmail.hasDocuments) return;

      this.invoiceEmail.sending = true;
      try {
        const { data } = await this.$http.post("/contrato-fatura/enviar-fatura", {
          id_contrato_fatura: this.invoiceEmail.contrato.id_contrato_fatura,
          competencia: this.invoiceEmail.competencia,
          email: this.invoiceEmail.email,
        });

        this.$toast.success(data.message || "Fatura enviada com sucesso!");
        this.$bvModal.hide("send-contract-invoice-modal");
      } catch (error) {
        const message = error?.response?.status === 401
          ? "Sua sessão expirou. Entre novamente no sistema para enviar a fatura."
          : error?.response?.data?.message || "Não foi possível enviar a fatura.";
        this.$toast.error(message);
      } finally {
        this.invoiceEmail.sending = false;
      }
    },
    resetInvoiceEmailModal() {
      this.invoiceEmail = {
        contrato: null,
        email: "",
        competencia: "",
        nfses: [],
        boletos: [],
        hasDocuments: false,
        loadError: "",
        loading: false,
        sending: false,
      };
    },
    jsonPretty(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },

    async handleImprimirNFSe(item) {
      if (!item) return;
      const id = item.id_nfse;
      this.printingId = id;

      try {
        if (item.nfse_url) {
          window.open(item.nfse_url, "_blank", "noopener");
          return;
        }

        this.loadingPrint = true;
        const res = await this.$http.post("/nfse/imprimir", { id_nfse: id });
        const url = res?.data?.nfse_url || res?.data?.url;

        if (url) {
          window.open(url, "_blank", "noopener");
          this.$toast?.success?.("PDF gerado com sucesso!");
        } else {
          this.$toast?.danger?.("Não foi possível gerar o PDF da NFSe.");
        }
      } catch (e) {
        this.$toast?.danger?.("Erro ao gerar o PDF da NFSe.");
      } finally {
        this.printingId = null;
        this.loadingPrint = false;
      }
    },

    abrirBoleto(b) {
      if (!b?.url) {
        this.$toast?.warning?.("Boleto sem URL.");
        return;
      }
      window.open(b.url, "_blank", "noopener");
    },

    async fetchKPIs() {
      this.loadingKpis = true;
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let dias = 30; // fallback padrão

        if (this.filters?.dateTo) {
          const end = new Date(this.filters.dateTo + "T00:00:00");
          const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
          dias = Number.isFinite(diffDays) ? Math.max(1, diffDays) : 30;
        }

        const { data } = await this.$http.post("/contrato-fatura/kpis", {
          dias_a_vencer: dias,
        });

        if (data) {
          this.kpis = {
            ativos: Number(data.ativos ?? 0),
            aVencer30: Number(data.aVencer ?? data.aVencer30 ?? 0),
            vencidos: Number(data.vencidos ?? 0),
            valorMensal: Number(data.valorMensal ?? 0),
            totalJanela: Number(data.totalJanela ?? 0),
            janela: data.janela ?? null,
          };
        }
      } finally {
        this.loadingKpis = false;
      }
    },

    getMonthStart(date = new Date()) {
      const y = date.getFullYear();
      const m = date.getMonth();
      return `${y}-${String(m + 1).padStart(2, "0")}-01`;
    },
    getMonthEnd(date = new Date()) {
      const y = date.getFullYear();
      const m = date.getMonth();
      const last = new Date(y, m + 1, 0).getDate();
      return `${y}-${String(m + 1).padStart(2, "0")}-${String(last).padStart(
        2,
        "0"
      )}`;
    },
    clearDateRange() {
      this.filters.dateFrom = null;
      this.filters.dateTo = null;
      this.sort = { field: null, type: null, typeDesc: false };
      this.applyFilters();
    },
    async fetchData() {
      this.loading = true;
      try {
        const payload = {
          columnFilters: this.buildColumnFilters(),
          sort: [{ type: this.sort.type, field: this.sort.field }],
          page: this.page,
          per_page: this.perPage,
        };
        const { data } = await this.$http.post(
          "/contrato-fatura/lista",
          payload
        );

        const mapped = (data.data || []).map((item) => {
          const dias = this.diffInDays(item.data_termino);
          return {
            ...item,
            __dias: dias,
            __servicos: this.safeJSON(item.servicos),
            __pagamentos: this.safeJSON(item.pagamentos),
          };
        });

        this.rows = mapped;
        this.pagination.total = data.total || 0;
        this.pagination.last_page = data.last_page || 1;
        this.page = data.current_page || this.page;
        this.perPage = Number(data.per_page || this.perPage);
      } catch (e) {
        this.$toast.danger("Não foi possível carregar os contratos.");
      } finally {
        this.loading = false;
      }
    },

    // ======= Filtros/Payload =======
    buildColumnFilters() {
      const filters = {};

      if (
        this.filters.status !== null &&
        this.filters.status !== undefined &&
        this.filters.status !== ""
      ) {
        filters.status = Number(this.filters.status);
      }

      const term = (this.searchTerm || "").trim();

      if (term) {
        const digits = term.replace(/\D/g, "");
        const isOnlyDigits = /^\d+$/.test(term);

        // CPF (11) ou CNPJ (14)
        if (digits.length === 11 || digits.length === 14) {
          filters.cnpj_cpf = digits;
          delete filters.cliente;
          delete filters.numero;
        }
        // Número de contrato: somente números e < 11 dígitos
        else if (isOnlyDigits && digits.length > 0 && digits.length < 11) {
          filters.numero = digits;
          delete filters.cliente;
          delete filters.cnpj_cpf;
        }
        // Nome do cliente (qualquer outro caso)
        else {
          filters.cliente = term;
          delete filters.cnpj_cpf;
          delete filters.numero;
        }
      }

      if (this.filters.dateFrom)
        filters.data_termino_from = this.filters.dateFrom;
      if (this.filters.dateTo) filters.data_termino_to = this.filters.dateTo;

      return filters;
    },

    // ======= Ações individuais =======
    excluir(data) {
      this.getRequestAdmin(() => {
        this.$swal
          .fire({
            icon: "warning",
            title: "Excluir o contrato: " + data.numero + "?",
            showCancelButton: true,
            confirmButtonText: `Sim`,
            cancelButtonText: `Não`,
            confirmButtonColor: "#f64e60",
          })
          .then(async (result) => {
            if (result.isConfirmed) {
              await this.$http.post("/contrato-fatura/delete", {
                id_contrato_fatura: data.id_contrato_fatura,
              });
              this.fetchData();
              this.$toast.success("Contrato excluído!");
            }
          });
      });
    },
    async editarContrato(data) {
      swal({
        title: "Deseja editar o contrato?",
        text: "Você ira ser direcionado para a pagina de edição aguarde...",
        icon: "info",
        buttons: {
          cancel: {
            text: "Cancelar",
            value: "cancel",
            visible: true,
            closeModal: true,
          },
          confirm: { text: "OK", visible: true, closeModal: false },
        },
        dangerMode: false,
        closeModal: false,
        closeOnClickOutside: false,
      }).then(async (value) => {
        if (value === "cancel") {
          swal.close();
          swal.stopLoading();
          return;
        }

        await this.$http
          .post("/contrato-fatura/find", {
            id_contrato_fatura: data.id_contrato_fatura,
          })
          .then((response) => {
            swal.stopLoading();
            swal.close();
            this.$toast.info("Você será redirecionado agora");
            this.$router.push({
              name: "contratosServicosNovo",
              params: {
                contratoData: {
                  ...response.data,
                  id_contrato_fatura: response.data.id_contrato_fatura,
                },
              },
            });
          })
          .catch(() => {
            swal.stopLoading();
            this.$toast.error("Erro ao solicitar o endpoint do Pedido");
            swal.close();
          });
      });
    },
    onSortChanged(ctx) {
      if (!ctx || !ctx.sortBy) return;

      const map = {
        vigencia: "data_termino", // <<< mapeia corretamente para o campo do banco
        cliente: "cliente",
        contrato: "numero",
        venda: "data_venda",
      };

      this.sort.field = map[ctx.sortBy] || ctx.sortBy;
      this.sort.typeDesc = !!ctx.sortDesc;
      this.sort.type = ctx.sortDesc ? "desc" : "asc";
      this.page = 1;
      this.fetchData();
    },

    applyFilters() {
      this.page = 1;
      this.fetchData();
    },
    resetFilters() {
      this.filters = { cliente: "", status: null };
      this.page = 1;
      this.fetchData();
    },
    changePerPage() {
      this.page = 1;
      this.fetchData();
    },
    formatMoney(v) {
      const n = Number(v || 0);
      return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    maskDoc(doc) {
      if (!doc) return "";
      const s = String(doc);
      if (s.length === 11)
        return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (s.length === 14)
        return s.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5"
        );
      return s;
    },
    mapStatus(code) {
      const dict = { 1: "Editando", 2: "Ativo", 3: "Cancelado", 4: "Vencido", 6: "Congelado" };
      return dict[code] || code;
    },
    statusVariant(code) {
      return (
        { 1: "secondary", 2: "success", 3: "danger", 9: "warning", 6: "warning" }[code] ||
        "secondary"
      );
    },
    diffInDays(dateString) {
      if (!dateString) return null;
      const end = new Date(dateString);
      if (isNaN(end)) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const diffMs = endDay - today;
      return Math.round(diffMs / (1000 * 60 * 60 * 24));
    },
    badgeVigencia(dias) {
      if (dias === null) return "";
      if (dias < 0) return `Venceu há ${Math.abs(dias)}d`;
      if (dias === 0) return "Vence hoje";
      return `Vence em ${dias}d`;
    },
    diasClasse(dias) {
      if (dias === null) return "text-muted";
      if (dias < 0) return "text-danger";
      if (dias <= 15) return "text-warning";
      return "text-muted";
    },
    safeJSON(payload) {
      try {
        if (!payload) return null;
        return typeof payload === "string" ? JSON.parse(payload) : payload;
      } catch (_) {
        return null;
      }
    },

    // ======= Seleção =======
    isRowDisabled(row) {
      return Number(row?.status) === 3;
    },
    isRowSelected(row) {
      return this.selectedIds.includes(row.id_contrato_fatura);
    },
    toggleRow(row) {
      if (this.isRowDisabled(row)) return;
      const id = row.id_contrato_fatura;
      const i = this.selectedIds.indexOf(id);
      if (i >= 0) this.selectedIds.splice(i, 1);
      else this.selectedIds.push(id);
    },
    toggleSelectAllOnPage(e) {
      const checked = e.target.checked;
      const pageIds = this.rows
        .filter((r) => !this.isRowDisabled(r))
        .map((r) => r.id_contrato_fatura);

      if (checked) {
        pageIds.forEach((id) => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
        });
      } else {
        this.selectedIds = this.selectedIds.filter(
          (id) => !pageIds.includes(id)
        );
      }
    },
    clearSelection() {
      this.selectedIds = [];
    },

    async handleCancelar() {
      this.cancelLoading = true;
      try {
        const ids = [...this.selectedIds];
        if (!ids.length) return;

        const payloadIds = ids.map((id) => ({ id }));

        await this.$http.post("/contrato-fatura/update-status", {
          ids: payloadIds,
          status: 3,
        });

        this.$toast.success("Contrato(s) cacelado(s)");
        this.clearSelection();
        this.fetchKPIs();
        this.fetchData();

        this.$bvModal.hide("confirm-bulk-cancelar");
      } catch (e) {
        this.$toast.error("Erro ao cancelar contratos");
      } finally {
        this.cancelLoading = false;
      }
    },

    async handleConfirmAtivar() {
      this.confirmLoading = true;
      try {
        const ids = [...this.selectedIds];
        if (!ids.length) return;

        const payloadIds = ids.map((id) => ({ id }));

        await this.$http.post("/contrato-fatura/update-status", {
          ids: payloadIds,
          status: 2,
        });

        this.$toast.success("Contratos ativados");
        this.clearSelection();
        this.fetchKPIs();
        this.fetchData();

        this.$bvModal.hide("confirm-bulk-ativar");
      } catch (e) {
        this.$toast.error("Erro ao ativar contratos");
      } finally {
        this.confirmLoading = false;
      }
    },

    async handleConfirmCongela() {
      this.confirmLoading = true;
      try {
        const ids = [...this.selectedIds];
        if (!ids.length) return;

        const payloadIds = ids.map((id) => ({ id }));

        await this.$http.post("/contrato-fatura/update-status", {
          ids: payloadIds,
          status: 6,
        });

        this.$toast.success("Contratos congelado");
        this.clearSelection();
        this.fetchKPIs();
        this.fetchData();

        this.$bvModal.hide("confirm-bulk-congelar");
      } catch (e) {
        this.$toast.error("Erro ao ativar contratos");
      } finally {
        this.confirmLoading = false;
      }
    },

    async bulkEmitirNFSe() {
      const ids = [...this.selectedIds];
      if (!ids.length) return;
      await this.$http.post("/contrato-fatura/bulk/emitir-nfse", { ids });
      this.$toast.success("Solicitada emissão de NFSe");
      this.clearSelection();
    },
    async bulkGerarBoletos() {
      const ids = [...this.selectedIds];
      if (!ids.length) return;
      await this.$http.post("/contrato-fatura/bulk/gerar-boletos", { ids });
      this.$toast.success("Solicitada geração de boletos");
      this.clearSelection();
    },
    openAlterarDiaModal() {
      if (!this.selectedCount) {
        this.$toast?.info?.("Selecione pelo menos um contrato.");
        return;
      }
      this.diaCobrancaForm.day = null;
      this.$bvModal.show("confirma-dia-cobranca");
    },

    async handleAlterarDiaCobranca() {
      const ids = [...this.selectedIds];
      if (!ids.length || !this.diaCobrancaForm.day) return;

      this.diaCobrancaLoading = true;
      try {
        const payloadIds = ids.map((id) => ({ id }));
        await this.$http.post("/contrato-fatura/alterar-dia-cobranca", {
          ids: payloadIds,
          dia_cobranca: Number(this.diaCobrancaForm.day),
        });

        this.$toast.success("Dia de cobrança atualizado com sucesso!");
        this.clearSelection();
        this.fetchKPIs();
        this.fetchData();
        this.$bvModal.hide("confirma-dia-cobranca");
      } catch (e) {
        this.$toast.error("Erro ao alterar dia de cobrança.");
      } finally {
        this.diaCobrancaLoading = false;
      }
    },

    resetDiaCobrancaModal() {
      this.diaCobrancaForm = { day: null };
      this.diaCobrancaLoading = false;
    },
    openAlterarDiaEnvioModal() {
      if (!this.selectedCount) {
        this.$toast?.info?.("Selecione pelo menos um contrato.");
        return;
      }

      this.diaEnvioFaturaForm.day = null;
      this.$bvModal.show("confirma-dia-envio-fatura");
    },

    async handleAlterarDiaEnvioFatura() {
      const ids = [...this.selectedIds];
      if (!ids.length || !this.diaEnvioFaturaForm.day) return;

      this.diaEnvioFaturaLoading = true;
      try {
        const payloadIds = ids.map((id) => ({ id }));
        await this.$http.post("/contrato-fatura/alterar-dia-envio-fatura", {
          ids: payloadIds,
          dia_envio_fatura: Number(this.diaEnvioFaturaForm.day),
        });

        this.$toast.success("Dia de envio da fatura atualizado com sucesso!");
        this.clearSelection();
        this.fetchData();
        this.$bvModal.hide("confirma-dia-envio-fatura");
      } catch (e) {
        this.$toast.error("Erro ao alterar dia de envio da fatura.");
      } finally {
        this.diaEnvioFaturaLoading = false;
      }
    },

    resetDiaEnvioFaturaModal() {
      this.diaEnvioFaturaForm = { day: null };
      this.diaEnvioFaturaLoading = false;
    },
  },
};
</script>

<style scoped>
.table-danger {
  --bs-table-accent-bg: rgba(220, 53, 69, 0.05);
}

.table-warning {
  --bs-table-accent-bg: rgba(255, 193, 7, 0.08);
}

.shimmer {
  position: relative;
  overflow: hidden;
  background: #e9ecef;
}

.shimmer::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.55),
      transparent);
  animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
}

.kpi-cell .card {
  height: 100%;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

/* Compacta espaçamentos e tipografia */
/* Larguras “em caracteres” para inputs compactos */
.wch-28 {
  width: 28ch !important;
}

.wch-16 {
  width: 16ch !important;
}

.wch-14 {
  width: 14ch !important;
}

/* Enxuga espaçamentos do card e form */
.card-body.py-2 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.form-group {
  margin-bottom: 0 !important;
}

/* Evita quebra no desktop; no mobile, quebrará naturalmente */
@media (min-width: 992px) {
  .flex-nowrap {
    flex-wrap: nowrap !important;
  }
}
</style>
